import useConfirmation from '@/hooks/use-confirmation';
import { useGuidePlayer } from '@/hooks/use-guide-player';
import { useProgramWorkflowActions } from '@/hooks/use-program-workflow-actions';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@/hooks/use-registration-dialog';
import { useUnsponsoredDialog } from '@/hooks/use-unsponsored-dialog';
import { useUpdateCircleRecommendationStatus } from '@/hooks/use-update-circle-recommendation-status';
import programMessages from '@/lang/messages/program';
import { useAnalyticsEventProps } from '@/libs/analytics/event-props-provider';
import {
    EnumDialogName,
    EnumButtonName,
    EventProperties,
} from '@calm/evexia-analytics';
import { RbcStatus } from '@calm/evexia-web-sdk';
import useIsContentLocked from '@rhg/hooks/use-is-content-locked';
import { useProvidedProgramContextWithWorkflow } from '@rhg/hooks/use-provided-program-context/with-workflow';
import { logger } from '@rhg/libs/logger';
import { LockThinOutline } from '@rhg/libs/material/icons';
import { getGuideId } from '@rhg/utils/media';
import { getProgramRoute, ProgramState } from '@rhg/utils/programs';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { ProgramContextAwareSessionCardProps } from '..';
import { useMindshiftSessionCardContext } from '../use-mindshift-session-card-context';

type SessionCardPlayHandlerProps = Pick<
    ProgramContextAwareSessionCardProps,
    'session' | 'trackProps' | 'onPlay' | 'progressSource'
>;
/**
 * Do not use this hook outside of SessionCard and its dependencies.
 */
export function useSessionCardPlayHandler({
    session,
    trackProps,
    onPlay,
    progressSource,
}: SessionCardPlayHandlerProps) {
    const { formatMessage } = useIntl();
    const { getIsContentLocked } = useIsContentLocked();
    const { push } = useRouter();
    const { openGuidePlayer } = useGuidePlayer();
    const { showUnsponsoredDialog } = useUnsponsoredDialog();
    const { showRegistrationDialog } = useRegistrationDialog();
    const { confirm } = useConfirmation();
    const {
        program: _program,
        recommendation,
        programMetadata,
        packClass,
        programType,
        programState,
        workflow,
        currentWorkflowStep,
        toolsByGuide,
    } = useProvidedProgramContextWithWorkflow();
    const { updateStatusAsync } = useUpdateCircleRecommendationStatus(
        recommendation?.recDefnId
    );

    const program = _program ?? programMetadata;

    const { isMindshiftSession, trackProps: mindshiftTrackProps } =
        useMindshiftSessionCardContext(session, packClass);

    // Surface-level pack/home-row analytics context (RowIndex / RowItemIndex /
    // PackItemName / PackItemType / PackDisplayType / PackClass) is supplied by
    // the composing surface via `trackProps` and forwarded into
    // `sessionTrackProps` below — cards stay generic and never silently inherit
    // that attribution from ambient providers.
    //
    // Feed pages, however, only expose their FeedID / FeedLayout / FeedHasTags /
    // TagName via an ambient `AnalyticsEventPropsProvider`, and Session : Started
    // fires later from the guide player (rendered outside that subtree) — so
    // those page-level feed props can't reach it any other way. Snapshot ONLY
    // the feed/tag props here at play time, at lowest precedence (explicit
    // `trackProps` and mindshift props still win). Feed props are undefined off
    // a feed page, so nothing leaks on Home/Discover.
    // `IsFeaturedFeed` is inert today — nothing sets it on the provider, so it
    // stays undefined and is filtered out below. It's forward-plumbing for a
    // pending BE-provided field; wired through now so the destination is ready
    // once the backend lands it.
    const { FeedID, FeedLayout, FeedHasTags, IsFeaturedFeed, TagName } =
        useAnalyticsEventProps();
    const feedTrackProps = useMemo<EventProperties>(
        () =>
            Object.fromEntries(
                Object.entries({
                    FeedID,
                    FeedLayout,
                    FeedHasTags,
                    IsFeaturedFeed,
                    TagName,
                }).filter(([, value]) => value !== undefined)
            ),
        [FeedID, FeedLayout, FeedHasTags, IsFeaturedFeed, TagName]
    );

    const { onJoinProgram } = useProgramWorkflowActions({
        program: _program,
        workflow,
        currentWorkflowStep,
        packClass,
    });

    const isLocked = getIsContentLocked({
        programState,
        programType,
    });

    const sessionId = getGuideId(session);
    const isClinicalSession = !!program?.isClinical;
    const isClinicalProgram = programType === 'clinical_program';
    const canInteract =
        !isClinicalProgram || programState === ProgramState.InProgress;
    const programPushRoute = isClinicalSession
        ? getProgramRoute(program, packClass)
        : undefined;

    const playHandler = useCallback(async () => {
        const mindshiftSessionTrackProps = isMindshiftSession
            ? mindshiftTrackProps
            : {};

        if (programPushRoute) {
            push(programPushRoute).catch((err) =>
                logger.error('Failed to navigate to program', {}, err as Error)
            );
        }

        if (isLocked) {
            if (await showUnsponsoredDialog()) {
                // Wait for the URL update so the registration dialog is fully
                // mounted before this handler returns — keeps the click flow
                // visually continuous instead of leaving a brief gap.
                await showRegistrationDialog({
                    currentStep: RegistrationFlowStep.AccessCode,
                    registrationFlow: RegistrationFlow.ExistingRegistration,
                });
            }
            return;
        }

        if (canInteract) {
            void openGuidePlayer({
                guide: session,
                packClass,
                workflow,
                tools: toolsByGuide[sessionId],
                willSkipToPreviousProgress: true,
                sessionTrackProps: {
                    ...feedTrackProps,
                    ...mindshiftSessionTrackProps,
                    ...trackProps,
                },
                progressSource,
            });
            /* Mark rec as viewed when user clicks on the card, to be used for determining plan completion metrics */
            // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
            updateStatusAsync({ status: RbcStatus.Viewed }).catch((err) =>
                logger.error(
                    'Failed to mark recommendation as viewed',
                    {},
                    err as Error
                )
            );
        } else if (
            await confirm({
                noCancelButton: true,
                icon: <LockThinOutline fontSize="large" color="inherit" />,
                iconColor: 'info',
                title: formatMessage(programMessages['program.join_program']),
                body: formatMessage(
                    programMessages['program.before_viewing_join']
                ),
                confirmLabel: formatMessage(
                    programMessages['program.join_program']
                ),
                trackDialog: EnumDialogName.JoinBeforeViewingProgram,
                confirmTrackButton: EnumButtonName.DialogJoinProgram,
            })
        ) {
            // TODO(EVE-10506): callers currently log failures to Datadog only; needs user-facing error UX
            onJoinProgram({
                onSuccess: () => {
                    void openGuidePlayer({
                        guide: session,
                        packClass,
                        workflow,
                        tools: toolsByGuide[sessionId],
                        willSkipToPreviousProgress: true,
                        sessionTrackProps: {
                            ...feedTrackProps,
                            ...mindshiftSessionTrackProps,
                            ...trackProps,
                        },
                        progressSource,
                    });
                },
                willResetProgress: false,
                selectedGuide: session,
            }).catch((err) =>
                logger.error('Failed to join program', {}, err as Error)
            );
        }

        onPlay?.();
    }, [
        packClass,
        programPushRoute,
        isLocked,
        onPlay,
        progressSource,
        push,
        showUnsponsoredDialog,
        showRegistrationDialog,
        canInteract,
        confirm,
        formatMessage,
        openGuidePlayer,
        session,
        workflow,
        toolsByGuide,
        trackProps,
        onJoinProgram,
        sessionId,
        isMindshiftSession,
        mindshiftTrackProps,
        feedTrackProps,
        updateStatusAsync,
    ]);

    return {
        playHandler,
    };
}
